import { useMemo } from "react";

export const useDamagedItems = (returns) => {
    // Filter returns that have damage reports or damaged quantities
    const damagedReturns = useMemo(() => {
        return returns?.filter(r => {
            // Check if return is explicitly a damage return
            if (r.rawType === 'damage') return true;

            // Check if return has damage report in original data
            if (r.originalData?.damageReport && r.originalData.damageReport.length > 0) {
                return true;
            }
            
            // Check if any items have damaged quantities in original data
            if (r.originalData?.items && r.originalData.items.some(item => (item.damagedQty && item.damagedQty > 0) || (item.damageReport && item.damageReport.length > 0))) {
                return true;
            }
            
            // Check if any items have damaged quantities in current items
            if (r.items && r.items.some(item => (item.damagedQty && item.damagedQty > 0) || (item.damageReport && item.damageReport.length > 0))) {
                return true;
            }
            
            // Check for damage report in notes text
            if (r.originalData?.notes?.includes('Damage Report:') || r.notes?.includes('Damage Report:')) {
                return true;
            }
            
            return false;
        }) || [];
    }, [returns]);

    const processDamagedReturn = (r) => {
        // Calculate damaged items summary from multiple sources
        const damageReport = r.originalData?.damageReport || [];
        const originalDamagedItems = (r.originalData?.items || []).filter(item => (item.damagedQty && item.damagedQty > 0) || (item.damageReport && item.damageReport.length > 0));
        const currentDamagedItems = r.rawType === 'damage' 
            ? (r.items || []).map(it => ({ ...it, damagedQty: it.qty || it.quantity || 0 }))
            : (r.items || []).filter(item => (item.damagedQty && item.damagedQty > 0) || (item.damageReport && item.damageReport.length > 0));
        
        // Parse damage count from notes text if no structured data
        let parsedDamagedItems = [];
        const notes = r.originalData?.notes || r.notes || '';
        if (notes.includes('Damage Report:') && damageReport.length === 0 && originalDamagedItems.length === 0 && currentDamagedItems.length === 0) {
            const damageMatch = notes.match(/Damage Report:\s*(\d+)\s*item\(s\)\s*damaged/i);
            if (damageMatch) {
                const damagedCount = parseInt(damageMatch[1]);
                
                // Try to extract damage reason from notes
                let damageReason = 'Damage reported during return processing';
                
                // Look for specific damage reasons in notes
                if (notes.includes('broken')) damageReason = 'Items broken during handling';
                else if (notes.includes('scratched')) damageReason = 'Items scratched during handling';
                else if (notes.includes('defective')) damageReason = 'Items found defective';
                else if (notes.includes('damaged')) {
                    // Extract the part before "Damage Report" as reason
                    const beforeDamageReport = notes.split('Damage Report:')[0].trim();
                    if (beforeDamageReport && beforeDamageReport.length > 0) {
                        damageReason = beforeDamageReport;
                    }
                }
                
                // Try to get actual item names from the return items
                const returnItems = r.originalData?.items || r.items || [];
                const actualItem = returnItems[0]; // Use first item as reference
                
                // Create damaged item entry with actual item name and extracted reason
                parsedDamagedItems = [{
                    damagedQty: damagedCount,
                    reason: damageReason,
                    item: { 
                        name: actualItem?.item?.name || actualItem?.itemName || actualItem?.name || 'Unknown Item',
                        _id: actualItem?.item?._id || actualItem?._id
                    }
                }];
            }
        }
        
        // Use damage report first, then fall back to damaged items, then parsed from notes
        const damagedItems = damageReport.length > 0 ? damageReport : [...originalDamagedItems, ...currentDamagedItems, ...parsedDamagedItems];
        
        // Get item names and reasons for damage report items
        const enrichedDamagedItems = damagedItems.map(item => {
            // Extract reason from multiple sources
            let itemReason = item.reason;
            
            // If no reason, try to extract from notes
            if (!itemReason) {
                if (notes.includes('broken')) itemReason = 'Items broken during handling';
                else if (notes.includes('scratched')) itemReason = 'Items scratched during handling';
                else if (notes.includes('defective')) itemReason = 'Items found defective';
                else if (notes.includes('damaged')) {
                    const beforeDamageReport = notes.split('Damage Report:')[0].trim();
                    if (beforeDamageReport && beforeDamageReport.length > 0) {
                        itemReason = beforeDamageReport;
                    }
                }
            }
            
            // Try to find item name and RATE from original items
            const originalItems = r.originalData?.items || r.items || [];
            let originalItem = null;
            
            // Try matching by item ID
            originalItem = originalItems.find(oi => {
                const oiInvId = oi.inventoryId?._id || oi.inventoryId;
                const oiItemId = oi.item?._id || oi.item;
                const targetId = item.item?._id || item.item || item.inventoryId?._id || item.inventoryId;
                
                if (!targetId) return false;
                
                return (
                    (oiInvId && oiInvId.toString() === targetId.toString()) ||
                    (oiItemId && oiItemId.toString() === targetId.toString()) ||
                    (oi._id && oi._id.toString() === targetId.toString())
                );
            });
            
            // If not found by ID, try to use the first item as fallback
            if (!originalItem && originalItems.length > 0) {
                originalItem = originalItems[0];
            }

            // Extract rate/price
            const itemData = originalItem?.item || {};
            const invData = originalItem?.inventoryId || {};
            
            const itemRate = originalItem 
                ? (Number(originalItem.rate) || Number(originalItem.unitPrice) || Number(originalItem.price) || 
                   Number(invData.purchaseRate) || Number(invData.unitPrice) || Number(invData.rate) || Number(invData.price) ||
                   Number(itemData.unitPrice) || Number(itemData.rate) || Number(itemData.price) || Number(itemData.purchasePrice) || 
                   Number(item.rate) || Number(item.unitPrice) || Number(item.price) || 
                   Number(r.unitPrice) || Number(r.price) || Number(r.rate) ||
                   (r.totalAmount && r.totalQty ? r.totalAmount / r.totalQty : 0) ||
                   0)
                : (Number(item.rate) || Number(item.unitPrice) || Number(item.price) || 
                   Number(r.unitPrice) || Number(r.price) || Number(r.rate) ||
                   (r.totalAmount && r.totalQty ? r.totalAmount / r.totalQty : 0) ||
                   0);
            
            if (originalItem) {
                return {
                    ...item,
                    reason: itemReason || 'Damage reported',
                    rate: itemRate,
                    item: {
                        ...(typeof item.item === 'object' ? item.item : {}),
                        name: originalItem.itemName || originalItem.item?.name || originalItem.item?.itemName || originalItem.inventoryId?.item?.itemName || originalItem.inventoryId?.item?.name || originalItem.name || 'Unknown Item',
                        _id: originalItem.inventoryId?._id || originalItem.item?._id || originalItem._id
                    }
                };
            }
            
            return {
                ...item,
                reason: itemReason || 'Damage reported',
                rate: itemRate
            };
        });
        
        const totalDamagedQty = enrichedDamagedItems.reduce((sum, item) => {
            let qty = Number(item.damagedQty) || 0;
            if (qty === 0 && item.damageReport && item.damageReport.length > 0) {
                qty = item.damageReport.reduce((acc, rep) => acc + (Number(rep.qty || rep.quantity) || 0), 0);
            }
            return sum + qty;
        }, 0);
        
        const totalDamagedAmount = enrichedDamagedItems.reduce((sum, item) => {
            let qty = Number(item.damagedQty) || 0;
            if (qty === 0 && item.damageReport && item.damageReport.length > 0) {
                qty = item.damageReport.reduce((acc, rep) => acc + (Number(rep.qty || rep.quantity) || 0), 0);
            }
            return sum + (qty * (Number(item.rate) || 0));
        }, 0);
        
        return {
            enrichedDamagedItems,
            totalDamagedQty,
            totalDamagedAmount
        };
    };

    return {
        damagedReturns,
        processDamagedReturn
    };
};
